import { useCallback, useEffect, useRef, useState } from 'react'
import { useAudioPlayer } from './useAudioPlayer'
import { quranApi } from '../lib/quranApi'
import { progressApi, surahsApi } from '../lib/api'
import { progressCreditOnAyahChange } from '../lib/progressCredit'
import type { Ayah, SurahPlaybackSettings, Translation } from '../types'

/**
 * Repeat behaviour at the end of the playlist queue.
 *  - `off`        : stop when the last surah ends
 *  - `repeat-all` : wrap to index 0 after the last surah
 *  - `repeat-one` : replay the current surah forever
 */
export type PlaylistRepeatMode = 'off' | 'repeat-all' | 'repeat-one'

const REPEAT_STORAGE_KEY = 'qlearn:playlist-repeat'

function isRepeatMode(v: unknown): v is PlaylistRepeatMode {
  return v === 'off' || v === 'repeat-all' || v === 'repeat-one'
}

function readPersistedRepeat(): PlaylistRepeatMode {
  if (typeof window === 'undefined') return 'off'
  try {
    const raw = window.localStorage.getItem(REPEAT_STORAGE_KEY)
    return isRepeatMode(raw) ? raw : 'off'
  } catch {
    return 'off'
  }
}

interface UsePlaylistPlayerOptions {
  /** Ordered surah numbers that make up the playlist queue. */
  surahs: number[]
  /**
   * Optional per-row playback settings keyed by surah number. Absent surahs
   * play with all defaults (full surah, 1× outer, 1× per-ayah).
   * The playlist-wide repeatMode still applies AFTER all individual repeats
   * are exhausted.
   */
  settingsBySurah?: Record<number, SurahPlaybackSettings>
  /** Reciter identifier (e.g. "sudais"). */
  reciter: string
  /** Translation edition (used when fetching surah data). */
  edition: string
  /**
   * When true, prepend Surah 1 Ayah 1 (Bismillah) before every surah in the
   * queue. Surah 1 is exempt — it already IS the Bismillah. Loaded lazily on
   * first use and cached per reciter so the prepend is free after the first
   * fetch.
   */
  bismillahBeforeSurah?: boolean
  /**
   * Whether the viewer is signed in. Gates progress reporting — there's no
   * point pinging `/progress/record` for an anonymous session (it has no
   * user to credit minutes/verses to).
   */
  isAuthenticated?: boolean
  /**
   * Called right after a surah's listen-count is recorded on the backend
   * (a playlist row finished all its programmed repeats). The provider wires
   * this to `refreshUser` so the per-surah "times played" badge updates
   * immediately — without it the DB increments but the badge, which reads
   * from the cached auth profile (`user.surahListenCounts`), stays stale
   * until the next full profile fetch (page reload).
   */
  onListenRecorded?: () => void
}

const MIN_REPEATS = 1
const MAX_REPEATS = 10

function resolveRepeats(
  map: Record<number, SurahPlaybackSettings> | undefined,
  surahNum: number,
): number {
  const raw = map?.[surahNum]?.repeats ?? MIN_REPEATS
  if (!Number.isFinite(raw) || raw < MIN_REPEATS) return MIN_REPEATS
  return Math.min(MAX_REPEATS, Math.floor(raw))
}

export type SurahRepeatPlaythrough = { current: number; total: number }

export function playthroughFromRemaining(
  remaining: number,
  total: number,
): SurahRepeatPlaythrough | null {
  if (total <= 1 || remaining < 1) return null
  return { current: total - remaining + 1, total }
}

/**
 * Build the effective ayah queue for a single surah given its settings.
 *
 * Implementation notes:
 *  - Ayah range (ayahFrom..ayahTo): slice the surah's full ayah list to
 *    that 1-based inclusive window. Out-of-range values fall back to "full
 *    surah" rather than producing an empty queue.
 *  - Per-ayah repeat: duplicate each ayah N times in place. The engine
 *    plays the duplicated array linearly; identical ayah objects share the
 *    same cached audio URL so duplicates cost zero extra network. This
 *    keeps the engine itself oblivious to per-ayah looping.
 */
function applySettingsToAyahs(
  ayahs: Ayah[],
  settings: SurahPlaybackSettings | undefined,
): Ayah[] {
  // Defensive: if upstream returned null/undefined (network glitch, malformed
  // CDN response, JSON-null from a backend nil slice), return an empty array
  // so the engine simply skips this surah instead of crashing the player.
  if (!ayahs || ayahs.length === 0) return ayahs ?? []
  let slice = ayahs
  const from = settings?.ayahFrom
  const to = settings?.ayahTo
  if (from && to && from >= 1 && to >= from) {
    // Clamp so an oversized 'to' (e.g. legacy data after we shrink a surah's
    // list) doesn't produce an empty slice.
    const start = Math.min(from - 1, ayahs.length - 1)
    const end = Math.min(to, ayahs.length)
    const candidate = ayahs.slice(start, end)
    if (candidate.length > 0) slice = candidate
  }
  const perAyah = settings?.perAyahRepeat
  if (perAyah && perAyah > 1) {
    const factor = Math.min(MAX_REPEATS, Math.max(MIN_REPEATS, Math.floor(perAyah)))
    const expanded: Ayah[] = []
    for (const a of slice) {
      for (let i = 0; i < factor; i++) expanded.push(a)
    }
    return expanded
  }
  return slice
}

/**
 * Wraps `useAudioPlayer` with playlist-aware navigation. The audio engine
 * stays focused on one surah at a time (its native unit); this hook plays
 * a CUSTOM sequence of surahs by:
 *   - feeding the engine the active surah's ayahs
 *   - intercepting end-of-surah via `fetchNextSurah` to return the NEXT
 *     surah in the playlist (not the natural surahNumber+1)
 *   - tracking which playlist index is currently playing
 *   - exposing playSurahAt(index) for direct row clicks
 *
 * Lives at the app root via `PlaylistAudioContext`, so playback survives
 * route changes (the engine inside `useAudioPlayer` is only disposed when
 * the provider itself unmounts).
 */
export function usePlaylistPlayer({
  surahs,
  settingsBySurah,
  reciter,
  edition,
  bismillahBeforeSurah = false,
  isAuthenticated = false,
  onListenRecorded,
}: UsePlaylistPlayerOptions) {
  const [activeSurahIndex, setActiveSurahIndex] = useState<number | null>(null)
  const [ayahs, setAyahs] = useState<Ayah[]>([])
  const [loadingSurah, setLoadingSurah] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [repeatMode, setRepeatModeState] = useState<PlaylistRepeatMode>(readPersistedRepeat)
  const [surahRepeatPlaythrough, setSurahRepeatPlaythrough] = useState<SurahRepeatPlaythrough | null>(null)
  // Forces a re-render when the edition-refetch effect (below) repopulates
  // translationsByKeyRef. That effect only mutates a ref — with no active
  // 4Hz playback tick (e.g. the player is PAUSED, so the audio engine's RAF
  // loop isn't running) nothing would otherwise repaint the "now playing"
  // panel, leaving it showing the wiped/blank translation until some
  // unrelated re-render happens to come along. The value itself is unused —
  // only the setter's re-render side effect matters.
  const [, bumpTranslationsVersion] = useState(0)

  // Latest playlist order in a ref so the queueProvider/onSurahHandoff
  // closures captured inside useAudioPlayer always see the current surahs
  // array (callers can reorder or remove items while playback is running).
  const surahsRef = useRef(surahs)
  surahsRef.current = surahs

  // Latest per-surah settings map in a ref for the same reason. Resolved via
  // the resolve* helpers (which clamp + default) every time we start a row.
  const settingsRef = useRef<Record<number, SurahPlaybackSettings> | undefined>(settingsBySurah)
  settingsRef.current = settingsBySurah

  // Mirrors the shaped `ayahs` state into a ref so playAyahInActiveSurah (and
  // any other synchronous, ref-only reader) always sees the CURRENT queue —
  // even in the narrow window where a natural end-of-surah handoff has
  // already updated activeIndexRef synchronously (onSurahHandoff, below) but
  // React hasn't re-rendered yet, so a `useCallback([ayahs])` closure would
  // still be bound to the OLD surah's array (activeIndexRef pointing at the
  // NEW surah + a stale ayahs array searched for the NEW surah's number
  // spuriously returns "not found"). Kept in sync at every point the shaped
  // queue changes: this per-render mirror, PLUS explicit synchronous
  // assignments in playSurahAt, onSurahHandoff, and stop() — each written
  // right alongside its corresponding setAyahs call so the two never
  // observe a torn/mixed state relative to each other.
  const shapedAyahsRef = useRef<Ayah[]>([])
  shapedAyahsRef.current = ayahs

  // Per-playlist-index "plays remaining" counter. Keyed by playlist index
  // (not surah number) so a playlist containing the same surah twice tracks
  // each row independently. Lives in a ref because it's mutated from inside
  // the audio-engine's `fetchNextSurah` closure, which must not trigger a
  // React re-render on every tick.
  const remainingRef = useRef<Record<number, number>>({})

  // FIX (auto-advance bug): the engine's `queueProvider` passes
  // `lastAyah.surahNumber + 1` to `fetchNextSurah` (it assumes natural
  // surah progression — that's the reader's contract). The playlist's
  // "what comes next" is decoupled from natural order, so we cannot use
  // the argument we receive — it would correspond to a surah that is
  // typically NOT in the playlist (e.g. playlist `[1, 5, 7]`, surah 1
  // ends → engine asks for "2", indexOf(2) === -1 → playlist halts).
  //
  // Instead we track our own "currently playing playlist index" in a ref
  // and resolve the next playlist item from THAT — independent of which
  // surah number the engine thinks comes next.
  const activeIndexRef = useRef<number | null>(null)
  activeIndexRef.current = activeSurahIndex

  const repeatModeRef = useRef(repeatMode)
  repeatModeRef.current = repeatMode

  // After the user clicks a row, we need to load the surah's ayahs then start
  // playback. State updates are async, so we set this flag and let an effect
  // trigger playAyah(0) once the new ayahs land in state.
  const pendingPlayRef = useRef(false)

  // Caches the last-known non-zero audio duration so onAyahChange can report
  // the listening time of the ayah that JUST FINISHED. When the engine flips
  // to the next ayah it fires onAyahChange synchronously, before React has
  // propagated the new buffer's duration — so the ref still holds the
  // previous ayah's length at that moment, which is exactly what we want to
  // credit. Mirrors the reader's recording path (QuranReaderPage).
  const lastAyahDurationRef = useRef(0)
  const progressVerseRef = useRef<{ surahNumber: number; ayahNumber: number } | null>(null)

  // Keep `isAuthenticated` readable from the (stable) audio-engine callbacks
  // without re-creating the engine when auth state flips.
  const isAuthenticatedRef = useRef(isAuthenticated)
  isAuthenticatedRef.current = isAuthenticated

  // Stable handle to the latest onListenRecorded so the (long-lived)
  // fetchNextSurah closure inside the audio engine always calls the current
  // callback without re-creating the engine.
  const onListenRecordedRef = useRef(onListenRecorded)
  onListenRecordedRef.current = onListenRecorded

  // Forward-ref to the audio player so playAyahInActiveSurah (defined with a
  // stable, dependency-free callback) can call playAyah without depending on
  // the `audioPlayer` object itself, which is a fresh literal every render.
  // The actual assignment happens just after useAudioPlayer returns.
  const audioPlayerRef = useRef<ReturnType<typeof useAudioPlayer> | null>(null)

  // Cached Bismillah (Surah 1, Ayah 1) for the "play Bismillah before each
  // surah" preference. Lazy: fetched on first surah load that needs it, then
  // reused for the rest of the session. The audio URL it produces (via the
  // engine's buildItem → getAudioUrl) is keyed by the ayah's surah/ayah
  // numbers, so the same cached Ayah object works for every reciter — the
  // engine reads the CURRENT reciter from the prefs at each play call.
  const bismillahRef = useRef<Ayah | null>(null)
  const bismillahLoadingRef = useRef<Promise<Ayah | null> | null>(null)
  const bismillahEnabledRef = useRef(bismillahBeforeSurah)
  bismillahEnabledRef.current = bismillahBeforeSurah

  // Translations keyed by "surahNumber:ayahNumber" so the "now playing" UI
  // can show the active verse's translation alongside its Arabic text. The
  // engine's queueProvider already fetches translations via
  // `quranApi.getSurahWithTranslation` — we just stash them here instead of
  // dropping them on the floor after surah load. O(1) lookup at render time.
  const translationsByKeyRef = useRef<Record<string, string>>({})
  // Soft cap on the cache. A user who plays the whole Quran in one session
  // would otherwise hold ~6,500 entries indefinitely — small per-entry but
  // unbounded. When we hit the cap we drop the lot; the engine re-populates
  // on the next surah load. Simple > LRU here since access is dominated by
  // the currently-playing surah's entries (only ~286 max).
  const TRANSLATION_CACHE_CAP = 2000
  const rememberTranslations = useCallback((surahNumber: number, translations: Translation[]) => {
    const cache = translationsByKeyRef.current
    for (const t of translations) {
      cache[`${surahNumber}:${t.ayahNumber}`] = t.text
    }
    if (Object.keys(cache).length > TRANSLATION_CACHE_CAP) {
      translationsByKeyRef.current = {}
    }
  }, [])

  // Invalidate cached Bismillah + translations when the user switches edition
  // mid-session — AND refetch for whatever is actively playing. Wiping alone
  // blanked the visible translation for the rest of the current surah (the
  // cache only repopulates at surah boundaries); the refetch keeps the
  // now-playing panel translated through the switch. Stale responses are
  // discarded if the edition changes again mid-fetch.
  useEffect(() => {
    bismillahRef.current = null
    bismillahLoadingRef.current = null
    translationsByKeyRef.current = {}
    const pos = activeIndexRef.current
    const active = pos !== null ? surahsRef.current[pos] : undefined
    if (active === undefined) return
    let cancelled = false
    const refetch = async (surahNumber: number) => {
      try {
        const data = await quranApi.getSurahWithTranslation(surahNumber, edition)
        if (!cancelled) {
          rememberTranslations(surahNumber, data.translations)
          // Re-render so a PAUSED player (no RAF tick running) still picks up
          // the freshly-cached translation, instead of showing blank/stale
          // text until some unrelated state change happens to repaint it.
          bumpTranslationsVersion((v) => v + 1)
        }
      } catch {
        // Best-effort: the next surah boundary repopulates with the new edition.
      }
    }
    void refetch(active)
    if (bismillahEnabledRef.current && active !== 1) void refetch(1)
    return () => { cancelled = true }
  }, [edition, rememberTranslations])

  // Fetch (or return cached) Surah 1 Ayah 1. Concurrent callers share the
  // same in-flight promise so we never issue more than one network request.
  // Returns null on failure — callers should gracefully skip the prepend
  // rather than block playback.
  const loadBismillah = useCallback(async (): Promise<Ayah | null> => {
    if (bismillahRef.current) return bismillahRef.current
    if (bismillahLoadingRef.current) return bismillahLoadingRef.current
    const promise = (async () => {
      try {
        const data = await quranApi.getSurahWithTranslation(1, edition)
        const first = data.ayahs[0]
        if (!first) return null
        bismillahRef.current = first
        // Cache Surah 1's translations so the "now playing" panel can show
        // the Bismillah's translation when it's playing as a preamble.
        rememberTranslations(1, data.translations)
        return first
      } catch {
        return null
      } finally {
        bismillahLoadingRef.current = null
      }
    })()
    bismillahLoadingRef.current = promise
    return promise
  }, [edition, rememberTranslations])

  // Prepend the cached Bismillah to a surah's shaped queue when the
  // preference is on AND the surah isn't Surah 1 itself (which IS the
  // Bismillah). Lazy-loads the Bismillah on first call; subsequent calls are
  // synchronous via the cached ref.
  const prependBismillah = useCallback(
    async (surahNumber: number, shaped: Ayah[]): Promise<Ayah[]> => {
      if (!bismillahEnabledRef.current) return shaped
      if (surahNumber === 1) return shaped
      const b = bismillahRef.current ?? (await loadBismillah())
      if (!b) return shaped
      return [b, ...shaped]
    },
    [loadBismillah],
  )

  function syncPlaythrough(index: number) {
    const list = surahsRef.current
    if (index < 0 || index >= list.length) {
      setSurahRepeatPlaythrough(null)
      return
    }
    const total = resolveRepeats(settingsRef.current, list[index])
    const remaining = remainingRef.current[index] ?? total
    setSurahRepeatPlaythrough(playthroughFromRemaining(remaining, total))
  }

  const audioPlayer = useAudioPlayer({
    ayahs,
    reciterEdition: reciter,
    // Row-level reciter override. Deliberately ignores the ayah's OWN
    // surahNumber — the Bismillah preamble ayah is always surah 1 / ayah 1,
    // but it must still play in the ACTIVE ROW's voice, not the global
    // default. Resolved purely from "which playlist row is active", read via
    // refs (activeIndexRef/surahsRef/settingsRef) so a synchronous handoff
    // (activeIndexRef is updated inside onSurahHandoff, below, before the
    // engine's next scheduleNext call) is seen immediately, even in the
    // window before React re-renders with the new activeSurahIndex state.
    resolveReciter: (_ayah) => {
      const pos = activeIndexRef.current
      const activeRowSurah = pos !== null ? surahsRef.current[pos] : undefined
      if (activeRowSurah === undefined) return reciter
      return settingsRef.current?.[activeRowSurah]?.reciter || reciter
    },
    onAyahChange: (_index, item) => {
      // Credit the ayah that just finished. `item` is the one that just
      // started; lastAyahDurationRef is still the finished length.
      if (!item.surahNumber || !item.ayahNumber) return
      const started = { surahNumber: item.surahNumber, ayahNumber: item.ayahNumber }
      if (isAuthenticatedRef.current) {
        const credit = progressCreditOnAyahChange(
          progressVerseRef.current,
          started,
          Math.round(lastAyahDurationRef.current),
        )
        if (credit) {
          progressApi.record(credit.surahNumber, credit.ayahNumber, credit.durationSec).catch(() => {
            /* best-effort — the Progress page catches up on the next entry */
          })
        }
      }
      progressVerseRef.current = started
    },
    fetchNextSurah: async () => {
      // NOTE: we deliberately ignore the engine's `currentSurahNumber` argument
      // (see comment on `activeIndexRef` above). The playlist is our source of
      // truth for "what plays next", not natural surah order.
      const list = surahsRef.current
      const pos = activeIndexRef.current
      if (pos === null || list.length === 0) return null

      // Per-row repeat: each playlist row plays N times before advancing.
      // We decrement on row COMPLETION (= end-of-surah event). If at least
      // one playthrough still remains AFTER the decrement, replay the same
      // row by returning the current surah; otherwise advance to the next
      // index (or apply the playlist-wide repeat-mode rules).
      //
      // The ayah list we hand back is the SHAPED list (sliced + per-ayah
      // duplicated) — the engine treats it as the surah's full queue and
      // plays it linearly, which gives us per-ayah looping without any
      // engine changes.
      const currentSurah = list[pos]
      const currentSettings = settingsRef.current?.[currentSurah]

      // One playthrough of currentSurah just finished (the engine calls
      // fetchNextSurah at end-of-surah). Count THIS playthrough so a surah
      // configured to repeat N× records N listens — matching the user's
      // expectation that "played 7×" shows as 7, not 1. Firing here (rather
      // than only after the final repeat) is what makes each repeat count.
      // Best-effort; the badge refresh (onListenRecorded) must not block
      // playback. recordListen is the surah-level counter, so per-ayah
      // repeats within a single playthrough correctly do NOT inflate it.
      surahsApi
        .recordListen(currentSurah)
        .then(() => onListenRecordedRef.current?.())
        .catch(() => {
          // Best-effort. The badge catches up on the next profile refetch.
        })

      const remainingBefore = remainingRef.current[pos] ?? resolveRepeats(settingsRef.current, currentSurah)
      const remainingAfter = remainingBefore - 1
      if (remainingAfter >= 1) {
        remainingRef.current[pos] = remainingAfter
        try {
          const data = await quranApi.getSurahWithTranslation(currentSurah, edition)
          rememberTranslations(currentSurah, data.translations)
          const shaped = applySettingsToAyahs(data.ayahs, currentSettings)
          // Bismillah plays before EACH iteration of a repeated surah so the
          // "every play of this surah opens with Bismillah" UX is consistent
          // — including the per-row repeat loop, not only the first play.
          const withPrefix = await prependBismillah(currentSurah, shaped)
          return {
            ayahs: withPrefix,
            surahData: { ...data, ayahs: withPrefix },
            // Same-row repeat: the "destination" IS the current row, but
            // carried explicitly for the same reason as the advance branch
            // below — computed here, synchronously, rather than resolved
            // later from a ref that a re-render could have moved on by then.
            reciter: settingsRef.current?.[currentSurah]?.reciter || reciter,
          }
        } catch {
          return null
        }
      }
      // Row finished all its programmed repeats — advance to the next item.
      // Each individual playthrough was already counted at the top of this
      // handler, so there's nothing to record here.
      remainingRef.current[pos] = 0

      const mode = repeatModeRef.current
      let nextPos: number
      if (mode === 'repeat-one') {
        nextPos = pos
      } else if (pos + 1 < list.length) {
        nextPos = pos + 1
      } else if (mode === 'repeat-all') {
        nextPos = 0
      } else {
        return null
      }

      // Seed the new row's remaining count. Stored eagerly so the engine's
      // very next `fetchNextSurah` call (after the new row's first playthrough)
      // sees the correct starting value rather than re-resolving from props.
      const nextSurahNum = list[nextPos]
      const nextSettings = settingsRef.current?.[nextSurahNum]
      remainingRef.current[nextPos] = resolveRepeats(settingsRef.current, nextSurahNum)
      try {
        const data = await quranApi.getSurahWithTranslation(nextSurahNum, edition)
        rememberTranslations(nextSurahNum, data.translations)
        const shaped = applySettingsToAyahs(data.ayahs, nextSettings)
        const withPrefix = await prependBismillah(nextSurahNum, shaped)
        return {
          ayahs: withPrefix,
          surahData: { ...data, ayahs: withPrefix },
          // Carried explicitly (rather than resolved later from
          // activeIndexRef) because useAudioPlayer builds THIS row's first
          // ayah URL immediately on this return, but activeIndexRef only
          // becomes the destination once onSurahHandoff runs — and it is
          // per-render mirrored from `activeSurahIndex` state
          // (`activeIndexRef.current = activeSurahIndex`, unconditionally,
          // every render), so any of the engine's RAF-driven re-renders in
          // between (routinely several per second while the outgoing ayah
          // keeps playing) resets it back to the OLD row first. Computing
          // the effective reciter here, synchronously, at the moment we
          // actually know the destination row, sidesteps that timing
          // window entirely.
          reciter: settingsRef.current?.[nextSurahNum]?.reciter || reciter,
        }
      } catch {
        return null
      }
    },
    onSurahHandoff: ({ surahData, surahNumber }) => {
      // Engine just rolled over to the next playlist item. Mirror its state
      // here so the UI's "now playing" row stays in sync. We bump
      // activeIndexRef BEFORE the engine schedules its next-after-this item,
      // so the next fetchNextSurah() call sees the freshly-advanced index.
      const data = surahData as
        | { ayahs?: Ayah[]; surah?: { number?: number } }
        | undefined
      if (data?.ayahs && data.ayahs.length > 0) {
        // Assign the ref SYNCHRONOUSLY, in the same tick as setAyahs — see
        // shapedAyahsRef's declaration above. activeIndexRef (below, also in
        // this same function) is updated synchronously too, so the two refs
        // never disagree with each other in the window before React re-renders.
        shapedAyahsRef.current = data.ayahs
        setAyahs(data.ayahs)
      }
      // `surahNumber` in the engine handoff is the FIRST ayah of the new
      // chunk. When `prependBismillah` injects a Bismillah ayah ahead of a
      // non-Fatiha surah, that first ayah carries surahNumber=1 — which
      // would resolve the playlist position to Al-Fatiha and strand the
      // row highlight there until the next handoff (which never fires
      // mid-chunk). The chunk's `surahData` is the raw quranApi response
      // shape `{ surah, ayahs, translations }`, so the actual loaded surah
      // is at `surahData.surah.number`, NOT `surahData.number` (an earlier
      // version of this fix read the wrong path and silently fell back to
      // the Bismillah-tainted `surahNumber`). Prefer the nested value so
      // listen counts and the active-row indicator key off the right surah.
      const loadedSurahNumber = data?.surah?.number ?? surahNumber
      const list = surahsRef.current
      const prevPos = activeIndexRef.current

      // Resolve the new playlist position. In `repeat-one` mode the surah
      // number is unchanged, so `indexOf(loadedSurahNumber)` would (correctly)
      // return the SAME index. In `repeat-all` the engine just looped us
      // back to index 0 — `indexOf` of the first surah would still find it.
      // For normal forward advance, the next index is prev+1 unless the
      // playlist has the same surah twice (then indexOf may yield the wrong
      // duplicate, so we prefer prev+1 when it matches).
      let newPos = list.indexOf(loadedSurahNumber)
      if (prevPos !== null) {
        const expectedNext = prevPos + 1 < list.length ? prevPos + 1 : 0
        if (list[expectedNext] === loadedSurahNumber) newPos = expectedNext
        else if (
          list[prevPos] === loadedSurahNumber &&
          repeatModeRef.current === 'repeat-one'
        ) {
          newPos = prevPos
        }
      }
      if (newPos !== -1) {
        activeIndexRef.current = newPos
        setActiveSurahIndex(newPos)
        syncPlaythrough(newPos)
      }
    },
  })
  // Populate the forward-ref now that useAudioPlayer has returned. See the
  // ref declaration above for why this indirection is needed.
  audioPlayerRef.current = audioPlayer

  // Keep the duration ref fresh. onAyahChange fires synchronously at the ayah
  // flip — before this effect runs for the new buffer — so when it reads the
  // ref it sees the PREVIOUS ayah's duration, which is the listening time we
  // want to credit. See lastAyahDurationRef.
  useEffect(() => {
    if (audioPlayer.duration > 0) {
      lastAyahDurationRef.current = audioPlayer.duration
    }
  }, [audioPlayer.duration])

  // Kick off playback once the freshly-loaded ayahs reach state. Only runs
  // when pendingPlayRef was set by a row-click handler.
  useEffect(() => {
    if (!pendingPlayRef.current) return
    if (ayahs.length === 0) return
    pendingPlayRef.current = false
    void audioPlayer.playAyah(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ayahs])

  const playSurahAt = useCallback(
    async (index: number) => {
      const list = surahsRef.current
      if (index < 0 || index >= list.length) return
      const surahNum = list[index]
      activeIndexRef.current = index
      setActiveSurahIndex(index)
      setError(null)
      setLoadingSurah(true)
      // Seed the per-row remaining counter from the configured repeats.
      // Manually jumping to a row resets its replay budget so the user
      // always gets the full programmed N plays starting from this click.
      remainingRef.current[index] = resolveRepeats(settingsRef.current, surahNum)
      syncPlaythrough(index)
      try {
        const data = await quranApi.getSurahWithTranslation(surahNum, edition)
        rememberTranslations(surahNum, data.translations)
        const shaped = applySettingsToAyahs(data.ayahs, settingsRef.current?.[surahNum])
        const withPrefix = await prependBismillah(surahNum, shaped)
        pendingPlayRef.current = true
        shapedAyahsRef.current = withPrefix
        setAyahs(withPrefix)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load surah')
      } finally {
        setLoadingSurah(false)
      }
    },
    [edition, prependBismillah, rememberTranslations]
  )

  const playFromStart = useCallback(() => {
    if (surahsRef.current.length === 0) return
    void playSurahAt(0)
  }, [playSurahAt])

  /**
   * Jump playback to a specific ayah of the ACTIVE surah's shaped queue
   * (Bismillah prefix, ayah ranges, and per-ayah repeats included — the
   * first matching slot wins, so a repeated ayah restarts at its first
   * repetition). Returns false (no-op) when nothing is active or the ayah
   * is not part of the shaped queue (e.g. outside an ayahFrom/ayahTo range).
   */
  const playAyahInActiveSurah = useCallback((ayahNumber: number): boolean => {
    const pos = activeIndexRef.current
    if (pos === null) return false
    const activeSurah = surahsRef.current[pos]
    if (activeSurah === undefined) return false
    // Read the ref mirror, NOT the `ayahs` state closure — see
    // shapedAyahsRef's declaration for why (stale-closure window across a
    // synchronous end-of-surah handoff).
    const index = shapedAyahsRef.current.findIndex(
      (a) => a.surahNumber === activeSurah && a.ayahNumber === ayahNumber,
    )
    if (index === -1) return false
    void audioPlayerRef.current?.playAyah(index)
    return true
  }, [])

  const nextSurah = useCallback(() => {
    if (activeSurahIndex === null) return
    const list = surahsRef.current
    if (activeSurahIndex + 1 < list.length) {
      void playSurahAt(activeSurahIndex + 1)
    } else if (repeatModeRef.current === 'repeat-all') {
      void playSurahAt(0)
    }
  }, [activeSurahIndex, playSurahAt])

  const prevSurah = useCallback(() => {
    if (activeSurahIndex === null) return
    if (activeSurahIndex > 0) {
      void playSurahAt(activeSurahIndex - 1)
    } else if (repeatModeRef.current === 'repeat-all') {
      void playSurahAt(surahsRef.current.length - 1)
    }
  }, [activeSurahIndex, playSurahAt])

  /** Cycle through repeat states: off → repeat-all → repeat-one → off … */
  const cycleRepeatMode = useCallback(() => {
    setRepeatModeState((cur) => {
      const nextMode: PlaylistRepeatMode =
        cur === 'off' ? 'repeat-all' : cur === 'repeat-all' ? 'repeat-one' : 'off'
      try {
        window.localStorage.setItem(REPEAT_STORAGE_KEY, nextMode)
      } catch {
        // localStorage unavailable (private mode) — state still updates
      }
      return nextMode
    })
  }, [])

  /** Stop playback and clear "currently playing" state. */
  const stop = useCallback(() => {
    audioPlayer.pause()
    setActiveSurahIndex(null)
    activeIndexRef.current = null
    shapedAyahsRef.current = []
    setAyahs([])
    remainingRef.current = {}
    setSurahRepeatPlaythrough(null)
  }, [audioPlayer])

  const activeSurahNumber =
    activeSurahIndex !== null && activeSurahIndex < surahs.length
      ? surahs[activeSurahIndex]
      : null

  // Currently-playing ayah text + translation, derived from the engine's
  // active queue item. `currentAyah` belongs to the SHAPED ayahs array
  // (may be Bismillah-prepended / per-ayah-duplicated), so its
  // surahNumber/ayahNumber identify the right translation entry stashed
  // by rememberTranslations. Null when idle or when a translation hasn't
  // been fetched yet for the active ayah's surah.
  const currentAyah = audioPlayer.currentAyah
  const currentAyahArabic = currentAyah?.text ?? null
  const currentAyahTranslation = currentAyah
    ? translationsByKeyRef.current[`${currentAyah.surahNumber}:${currentAyah.ayahNumber}`] ?? null
    : null

  // Arabic of the NEXT queued ayah (same shaped array the engine plays), used
  // by the immersive lyrics view to show a grayed "coming up" preview. Null at
  // the end of the current row (the next surah loads on handoff).
  const nextAyah = ayahs[audioPlayer.currentAyahIndex + 1] ?? null
  const nextAyahArabic = nextAyah?.text ?? null

  return {
    audioPlayer,
    activeSurahIndex,
    activeSurahNumber,
    ayahs,
    loadingSurah,
    error,
    repeatMode,
    cycleRepeatMode,
    playSurahAt,
    playFromStart,
    playAyahInActiveSurah,
    nextSurah,
    prevSurah,
    stop,
    surahRepeatPlaythrough,
    currentAyahArabic,
    currentAyahTranslation,
    /** Arabic of the next queued ayah (for the immersive "coming up" preview); null at row end. */
    nextAyahArabic,
    /** surahNumber of the ACTUAL playing ayah (may differ from activeSurahNumber during Bismillah preamble). */
    currentAyahSurahNumber: currentAyah?.surahNumber ?? null,
    /** ayahNumber of the actual playing ayah. */
    currentAyahNumber: currentAyah?.ayahNumber ?? null,
  }
}

